import React from 'react';
import { format } from 'date-fns';
import type { Lesson, Term } from '@court-sync/shared';
import {
  generateClinicOccurrences,
  parseLocalDateYmd,
  isCampFullPackageFromDaySelection,
} from '@court-sync/shared';
import { useAcademyPrimaryColor } from '../../../contexts/AcademyBrandingContext';
import { formatTimeHHMM } from '../../../utils/timeFormat';
import { Button } from '@/components/ui';

export interface ClinicSessionPickerStepProps {
  lesson: Lesson;
  /** Program date range for term-bound lessons. Omitted for camps — use lesson start/end dates. */
  term: Term | null;
  formatDate: (date: Date, region?: string | null, options?: Intl.DateTimeFormatOptions) => string;
  onSelect: (dates: string[]) => void;
  onBack: () => void;
  /** Restores checkboxes when returning from a later step (e.g. Back from account form). */
  initialSelectedDates?: string[];
  /** Dates that cannot be selected (already booked or full-term enrolled). */
  unavailableDates?: string[];
  /** Shown when all sessions are unavailable (e.g. full-term booking exists). */
  unavailableNotice?: string | null;
  /** When true, user cannot proceed (e.g. already fully enrolled). */
  disableContinue?: boolean;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function resolveSessionUnitPrice(lesson: Lesson): number | null {
  if (lesson.lessonType === 'group' && lesson.sessionPrice != null) {
    return lesson.sessionPrice;
  }
  if (lesson.lessonType === 'camp' && lesson.campDayPrice != null) {
    return lesson.campDayPrice;
  }
  if (lesson.pricePerSession != null) {
    return lesson.pricePerSession;
  }
  return null;
}

export function ClinicSessionPickerStep({
  lesson,
  term,
  formatDate,
  onSelect,
  onBack,
  initialSelectedDates,
  unavailableDates = [],
  unavailableNotice = null,
  disableContinue = false,
}: ClinicSessionPickerStepProps) {
  const sessionNoun = lesson.lessonType === 'camp' ? 'day' : 'session';

  const dateRange =
    term != null
      ? { startDate: term.startDate, endDate: term.endDate }
      : lesson.lessonType === 'camp' && lesson.startDate && lesson.endDate
        ? { startDate: lesson.startDate, endDate: lesson.endDate }
        : null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingOccurrences =
    lesson.lessonType === 'camp' && lesson.campSessionDates?.length
      ? lesson.campSessionDates
          .map((d) => parseLocalDateYmd(d))
          .filter((d): d is Date => d !== null && d >= today)
          .sort((a, b) => a.getTime() - b.getTime())
          .map((date) => ({ date, status: 'scheduled' as const }))
      : dateRange != null
        ? generateClinicOccurrences(lesson, dateRange).filter(
            (occ) => occ.status === 'scheduled' && occ.date >= today
          )
        : [];

  const allDateStrs = upcomingOccurrences.map((occ) => format(occ.date, 'yyyy-MM-dd'));
  const [selected, setSelected] = React.useState<Set<string>>(() => {
    const fromParent = (initialSelectedDates ?? []).filter((d) => allDateStrs.includes(d));
    return new Set(fromParent);
  });
  const primaryColor = useAcademyPrimaryColor();

  const unavailableSet = React.useMemo(() => new Set(unavailableDates), [unavailableDates]);
  const selectableDateStrs = allDateStrs.filter((d) => !unavailableSet.has(d));
  const allSelected =
    selectableDateStrs.length > 0 && selectableDateStrs.every((d) => selected.has(d));
  const unitPrice = resolveSessionUnitPrice(lesson);
  const fullCampPrice =
    lesson.lessonType === 'camp' ? (lesson.campFullPrice ?? lesson.price ?? null) : null;
  const usesFullCampPackagePrice =
    lesson.lessonType === 'camp' &&
    isCampFullPackageFromDaySelection(lesson, [...selected], unavailableDates);
  const totalPrice =
    usesFullCampPackagePrice && fullCampPrice != null
      ? fullCampPrice
      : unitPrice != null && selected.size > 0
        ? unitPrice * selected.size
        : null;

  const toggleDate = (dateStr: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableDateStrs));
    }
  };

  const emptyMessage =
    lesson.lessonType === 'camp'
      ? 'No upcoming days available for this camp.'
      : 'No upcoming sessions available for this lesson.';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Choose Sessions</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select one or more {sessionNoun}s of <strong>{lesson.name}</strong> to book.
          {unitPrice != null && (
            <>
              {' '}
              Each {sessionNoun} is <strong>${unitPrice}</strong>.
              {lesson.lessonType === 'camp' && fullCampPrice != null ? (
                <>
                  {' '}
                  Select all days to book the full camp package for <strong>${fullCampPrice}</strong>.
                </>
              ) : null}
            </>
          )}
        </p>
      </div>

      {unavailableNotice && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          {unavailableNotice}
        </p>
      )}

      {upcomingOccurrences.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm font-medium hover:underline"
            style={{ color: primaryColor }}
          >
            <span
              className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
              style={
                allSelected
                  ? { backgroundColor: primaryColor, borderColor: primaryColor }
                  : selected.size > 0
                    ? { backgroundColor: `${primaryColor}4d`, borderColor: primaryColor }
                    : { borderColor: 'var(--muted-foreground)' }
              }
            >
              {(allSelected || selected.size > 0) && (
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                  <path
                    d="M10 3L5 8.5 2 5.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>

          <div className="grid gap-2">
            {upcomingOccurrences.map((occ) => {
              const dateStr = format(occ.date, 'yyyy-MM-dd');
              const isUnavailable = unavailableSet.has(dateStr);
              const isChecked = selected.has(dateStr);
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => !isUnavailable && toggleDate(dateStr)}
                  disabled={isUnavailable}
                  className={`flex items-center gap-3 p-4 border rounded-lg transition-colors text-left w-full ${
                    isUnavailable
                      ? 'cursor-not-allowed border-border/60 bg-muted/40 opacity-60'
                      : !isChecked
                        ? 'border-border hover:bg-accent'
                        : ''
                  }`}
                  style={
                    isChecked
                      ? { borderColor: primaryColor, backgroundColor: `${primaryColor}0d` }
                      : undefined
                  }
                >
                  <span
                    className="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors border-muted-foreground"
                    style={
                      isChecked
                        ? { backgroundColor: primaryColor, borderColor: primaryColor }
                        : undefined
                    }
                  >
                    {isChecked && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                        <path
                          d="M10 3L5 8.5 2 5.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {DAY_NAMES[occ.date.getDay()]},{' '}
                      {formatDate(occ.date, null, { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatTimeHHMM(lesson.startTime)} – {formatTimeHHMM(lesson.endTime)}
                    </p>
                    {isUnavailable && (
                      <p className="text-xs text-muted-foreground mt-1">Already booked</p>
                    )}
                  </div>
                  {unitPrice != null && (
                    <span className="text-sm font-semibold text-green-600 dark:text-green-500 shrink-0">
                      ${unitPrice}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selected.size > 0 && totalPrice != null && (
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {usesFullCampPackagePrice
                  ? 'Full camp package'
                  : `${selected.size} ${sessionNoun}${selected.size !== 1 ? 's' : ''} selected`}
              </span>
              <span className="font-semibold text-foreground">Total: ${totalPrice}</span>
            </div>
          )}
        </>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Back
        </Button>
        <Button
          onClick={() => onSelect(Array.from(selected).sort())}
          disabled={selected.size === 0 || disableContinue}
          className="flex-1"
        >
          Continue{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
      </div>
    </div>
  );
}
